import './i18n';
import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import { AuthModalProvider } from "./context/AuthModalContext";
import { AccessibilityProvider } from "./context/AccessibilityContext";
import { useAuth } from "./context/AuthContext";
import { useAuthModal } from "./context/AuthModalContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import LoginModal from "./components/LoginModal";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Welcome from "./pages/Welcome";
import Dashboard from "./pages/Dashboard";
import Pricing from "./pages/Pricing";
import CapitalGain from "./pages/CapitalGain";
import TDSCalculator from "./pages/TDSCalculator";
import AccountSettings from "./pages/account/Settings";
import Accessibility from "./pages/account/Accessibility";
import Reports from "./pages/account/Reports";
import BankStatement from "./pages/BankStatement";
import AccessibilityPublic from "./pages/AccessibilityPublic";
import Plans from "./pages/account/Plans";
import AdminDashboard from "./pages/admin/AdminDashboard";
import PropertyGain from "./pages/PropertyGain";
import { AnimatePresence, motion } from "motion/react";

function SmartHome() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
      <div style={{ width: "32px", height: "32px", border: "3px solid var(--border)", borderTop: "3px solid #C9A84C", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    </div>
  );
  if (user) return <Navigate to="/dashboard" replace />;
  return <Home />;
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/login" element={<Login />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/" element={
          <Layout>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <SmartHome />
            </motion.div>
          </Layout>
        } />
        <Route path="/dashboard" element={
          <Layout>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <Dashboard />
            </motion.div>
          </Layout>
        } />
        <Route path="/pricing" element={
          <Layout>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <Pricing />
            </motion.div>
          </Layout>
        } />
        <Route path="/tools/capital-gain" element={
          <Layout>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <CapitalGain />
            </motion.div>
          </Layout>
        } />
        <Route path="/tools/tds-calculator" element={
          <Layout>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <TDSCalculator />
            </motion.div>
          </Layout>
        } />
        <Route path="/account/settings" element={
          <Layout>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <AccountSettings />
            </motion.div>
          </Layout>
        } />
        <Route path="/account/accessibility" element={
          <Layout>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <Accessibility />
            </motion.div>
          </Layout>
        } />
        <Route path="/account/reports" element={
          <Layout>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <Reports />
            </motion.div>
          </Layout>
        } />
        <Route path="/tools/property-gain" element={
          <Layout>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <PropertyGain />
            </motion.div>
          </Layout>
        } />
        <Route path="/tools/bank-statement" element={
          <Layout>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <BankStatement />
            </motion.div>
          </Layout>
        } />
        <Route path="/accessibility" element={
          <Layout>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <AccessibilityPublic />
            </motion.div>
          </Layout>
        } />
        <Route path="/account/plans" element={
          <Layout>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <Plans />
            </motion.div>
          </Layout>
        } />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="*" element={
          <Layout>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <SmartHome />
            </motion.div>
          </Layout>
        } />
      </Routes>
    </AnimatePresence>
  );
}

function Layout({ children, showNav = true }: { children: React.ReactNode; showNav?: boolean }) {
  const { user } = useAuth();
  const { openModal } = useAuthModal();
  const [isGuest, setIsGuest] = useState(() => localStorage.getItem("isGuest") === "true");
  const [bannerDismissed, setBannerDismissed] = useState(() => localStorage.getItem("guestBannerDismissed") === "true");

  useEffect(() => {
    const handler = () => {
      setIsGuest(localStorage.getItem("isGuest") === "true");
      setBannerDismissed(localStorage.getItem("guestBannerDismissed") === "true");
    };
    window.addEventListener("guestStateChanged", handler);
    return () => window.removeEventListener("guestStateChanged", handler);
  }, []);

  useEffect(() => {
    if (user) {
      localStorage.removeItem("isGuest");
      localStorage.removeItem("guestBannerDismissed");
      setIsGuest(false);
    }
  }, [user]);

  const showGuestBanner = isGuest && !user && !bannerDismissed;

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {showNav && <Navbar />}
      {showGuestBanner && (
        <div style={{
          background: "#FFF2CC",
          borderBottom: "1px solid rgba(201,168,76,0.4)",
          height: "40px",
          display: "flex",
          alignItems: "center",
          padding: "0 20px",
          fontSize: "13px",
          color: "#5C3D00",
          gap: "12px",
          flexShrink: 0,
        }}>
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            You are browsing as a guest. Login to save your reports and access all features.
          </span>
          <button
            onClick={openModal}
            style={{ color: "#7F6000", fontWeight: 700, background: "none", border: "none", cursor: "pointer", fontSize: "13px", whiteSpace: "nowrap", textDecoration: "underline", flexShrink: 0 }}
          >
            Login Now
          </button>
          <button
            onClick={() => { localStorage.setItem("guestBannerDismissed", "true"); setBannerDismissed(true); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#7F6000", fontSize: "16px", lineHeight: 1, padding: "0 4px", flexShrink: 0 }}
            title="Dismiss"
          >
            ✕
          </button>
        </div>
      )}
      <main style={{ flex: 1 }}>{children}</main>
      {showNav && <Footer />}
      <WhatsAppButton />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AuthModalProvider>
          <AccessibilityProvider>
            <Router>
              <AnimatedRoutes />
              <LoginModal />
            </Router>
          </AccessibilityProvider>
        </AuthModalProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
